import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext, useDroppable, PointerSensor, KeyboardSensor,
  useSensor, useSensors, DragOverlay, closestCorners,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Users, CheckCircle2, MinusCircle, Network, AlertTriangle, GripVertical,
  Plus, Pencil, Trash2, MoreVertical, Search, UserPlus, RefreshCw,
  ChevronLeft, ChevronRight, CalendarDays, Info, Loader2, Circle,
  ImagePlus, UserMinus, RotateCcw, MapPin, ExternalLink, Upload, Link2, ChevronDown,
  UserCheck,
} from "lucide-react";
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear,
  addDays, addWeeks, addMonths, addYears, format as fmtDate,
} from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, PageBody } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */
const CHEF_COLORS = ["#EE722E", "#2F6FED", "#1F9D55", "#F89F63", "#8B5CF6", "#0EA5A5", "#D64545", "#B26A00"];

function hexToRgba(hex, a) {
  try {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  } catch { return `rgba(238,114,46,${a})`; }
}
function initials(name) {
  return (name || "?").trim().split(/\s+/).map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}
function photoSrc(card) {
  // Priorité : upload admin (photo_path) → lien admin (photo_url) → PREREC (prerec_photo_url).
  if (card?.photo_path) {
    const t = localStorage.getItem("shariti_token");
    return `${API}/teams/photo/${card.photo_path}${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
  }
  if (card?.photo_url) return card.photo_url;
  if (card?.prerec_photo_url) return card.prerec_photo_url;
  return null;
}
const RecruiterAvatar = ({ card, size = 36, borderColor, bg }) => {
  const [err, setErr] = useState(false);
  const src = photoSrc(card);
  const style = { width: size, height: size };
  if (src && !err) {
    return <img src={src} alt={card.name} onError={() => setErr(true)}
      className="shrink-0 rounded-full border object-cover"
      style={{ ...style, borderColor: borderColor || "var(--border-soft)" }} />;
  }
  return (
    <span className="grid shrink-0 place-items-center rounded-full border text-[11px] font-bold text-[var(--shariti-ink)]"
      style={{ ...style, background: bg || "var(--surface-2)", borderColor: borderColor || "var(--border-soft)" }}>
      {initials(card.name)}
    </span>
  );
};

const PERIOD_MODES = [
  { v: "jour", l: "Jour" },
  { v: "semaine", l: "Semaine" },
  { v: "mois", l: "Mois" },
  { v: "annee", l: "Année" },
  { v: "perso", l: "Période" },
];
function periodRange(mode, anchor, range) {
  if (mode === "semaine") return { start: startOfWeek(anchor, { weekStartsOn: 1 }), end: endOfWeek(anchor, { weekStartsOn: 1 }) };
  if (mode === "mois") return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
  if (mode === "annee") return { start: startOfYear(anchor), end: endOfYear(anchor) };
  if (mode === "perso") { const f = range?.from || anchor; return { start: f, end: range?.to || f }; }
  return { start: anchor, end: anchor };
}
function periodLabel(mode, anchor, range) {
  if (mode === "semaine") {
    const s = startOfWeek(anchor, { weekStartsOn: 1 }); const e = endOfWeek(anchor, { weekStartsOn: 1 });
    return `${fmtDate(s, "d MMM", { locale: frLocale })} – ${fmtDate(e, "d MMM yyyy", { locale: frLocale })}`;
  }
  if (mode === "mois") return fmtDate(anchor, "MMMM yyyy", { locale: frLocale });
  if (mode === "annee") return fmtDate(anchor, "yyyy", { locale: frLocale });
  if (mode === "perso") {
    if (range?.from && range?.to) return `${fmtDate(range.from, "d MMM", { locale: frLocale })} – ${fmtDate(range.to, "d MMM yyyy", { locale: frLocale })}`;
    if (range?.from) return fmtDate(range.from, "d MMM yyyy", { locale: frLocale });
    return "Choisir une période…";
  }
  return fmtDate(anchor, "EEEE d MMMM yyyy", { locale: frLocale });
}
const isoDay = (d) => fmtDate(d, "yyyy-MM-dd");

/* ------------------------------------------------------------------ */
/* KPI strip                                                          */
/* ------------------------------------------------------------------ */
const KpiCard = ({ label, value, hint, icon: Icon, accent, testid, onClick }) => {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper onClick={onClick} data-testid={onClick ? `${testid}-button` : undefined}
      className={`card-hover w-full rounded-2xl border border-[var(--border-soft)] bg-white text-left shadow-[var(--shadow-sm)] border-l-[3px] ${accent} ${onClick ? "cursor-pointer hover:shadow-[var(--shadow-md)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(238,114,46,0.22)]" : ""}`}>
      <div className="flex items-start gap-3 p-3 sm:p-4">
        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--surface-2)]">
          <Icon className="h-5 w-5 text-[var(--shariti-ink)]" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-ink">{label}</div>
          <div className="text-2xl font-extrabold tabular-nums leading-tight sm:text-3xl" data-testid={testid}>{value}</div>
          {hint && <div className="text-[10px] text-muted-ink">{hint}</div>}
        </div>
      </div>
    </Wrapper>
  );
};

/* ------------------------------------------------------------------ */
/* Recruiter card (draggable)                                         */
/* ------------------------------------------------------------------ */
const PresenceBadge = ({ card, canEdit, onToggle }) => {
  const present = card.present;
  const cls = present
    ? "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F] border-[rgba(31,157,85,0.25)]"
    : "bg-[rgba(24,24,24,0.06)] text-[rgba(24,24,24,0.56)] border-[rgba(24,24,24,0.12)]";
  const label = present ? "Présent" : "Absent";
  const title = card.presence_source === "pointage" ? "Pointage terrain" : card.presence_source === "manuel" ? "Saisie manuelle" : "Non pointé";
  return (
    <Badge
      variant="outline"
      onClick={canEdit ? (e) => { e.stopPropagation(); onToggle(card); } : undefined}
      className={`gap-1 border ${cls} ${canEdit ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
      title={title}
      data-testid="equipes-recruiter-presence-badge"
    >
      <Circle className={`h-2 w-2 ${present ? "fill-[#1F9D55] text-[#1F9D55]" : "fill-[rgba(24,24,24,0.35)] text-[rgba(24,24,24,0.35)]"}`} />
      {label}
    </Badge>
  );
};

const PeriodStat = ({ card }) => {
  const pd = card.presence_days ?? 0;
  const sd = card.scheduled_days ?? 0;
  const rate = sd > 0 ? Math.round((pd / sd) * 100) : null;
  return (
    <div className="mt-1" data-testid="equipes-recruiter-period-stat">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-ink">
        <CheckCircle2 className="h-3 w-3 text-[#1F9D55]" />
        {sd > 0 ? <>Présent {pd}/{sd} j · {rate}%</> : <>{pd} j présent{pd > 1 ? "s" : ""}</>}
      </div>
      {sd > 0 && (
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
          <div className="h-full rounded-full bg-[#1F9D55] transition-[width] duration-200" style={{ width: `${Math.min(100, rate || 0)}%` }} />
        </div>
      )}
    </div>
  );
};

/* Spot du jour (par personne). Recruteur : le Pointage prime (badge « auto »),
   sinon saisie manuelle. Chef : saisie manuelle uniquement. Édition admin via
   Popover ; en lecture seule, simple affichage. */
const SpotJourEditor = ({ personId, personType, value, source, canEdit, date, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(value || ""); }, [value, open]);
  const isPointage = source === "pointage";

  const save = async (nextText) => {
    setSaving(true);
    try {
      const res = await api.post("/teams/spot-jour", {
        person_id: personId, person_type: personType, spot_text: nextText, date,
      });
      onSaved?.(personId, res.data.spot_du_jour, res.data.spot_source);
      setOpen(false);
      toast.success("Spot du jour mis à jour");
    } catch { toast.error("Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  const display = (
    <span className="inline-flex min-w-0 items-center gap-1 text-[11px] font-semibold">
      <MapPin className="h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
      {value
        ? <span className="truncate" title={value}>{value}</span>
        : <span className="text-muted-ink">{canEdit ? "Définir le spot" : "—"}</span>}
      {isPointage && (
        <span className="shrink-0 rounded bg-[rgba(31,157,85,0.12)] px-1 py-0.5 text-[9px] font-bold uppercase text-[#0F5A2F]">auto</span>
      )}
    </span>
  );

  if (!canEdit) {
    return <div className="mt-1" data-testid="equipes-spot-jour">{display}</div>;
  }
  return (
    <div className="mt-1" data-testid="equipes-spot-jour">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" onClick={(e) => e.stopPropagation()}
            className="inline-flex max-w-full items-center gap-1 rounded-md px-1 py-0.5 text-left transition-colors hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(238,114,46,0.35)]"
            data-testid="equipes-spot-jour-trigger">
            {display}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64" onClick={(e) => e.stopPropagation()} data-testid="equipes-spot-jour-popover">
          <div className="space-y-2">
            <div className="text-xs font-bold">Spot du jour</div>
            {isPointage && (
              <div className="flex items-start gap-1 rounded-md bg-[rgba(31,157,85,0.08)] px-2 py-1 text-[10px] leading-snug text-[#0F5A2F]">
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                Valeur issue du Pointage terrain — elle prime sur toute saisie manuelle ce jour.
              </div>
            )}
            <Input value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Ex : Gare du Nord" className="h-9" autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(text.trim()); } }}
              data-testid="equipes-spot-jour-input" />
            <div className="flex items-center justify-between gap-2">
              <Button variant="ghost" size="sm" className="text-muted-ink"
                disabled={saving || !value} onClick={() => save("")} data-testid="equipes-spot-jour-clear">Effacer</Button>
              <Button size="sm" className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]"
                disabled={saving} onClick={() => save(text.trim())} data-testid="equipes-spot-jour-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};


const RecruiterCard = ({ card, chefColor, canEdit, periodMulti, spotDate, onToggle, onPhoto, onOffRoster, onSaveSpot }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.rd_id, disabled: !canEdit,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };
  const avatarBg = chefColor ? hexToRgba(chefColor, 0.16) : "var(--surface-2)";
  const avatarBorder = chefColor ? hexToRgba(chefColor, 0.35) : "var(--border-soft)";
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`card-hover flex items-start gap-3 rounded-xl border border-[var(--border-soft)] bg-white p-2.5 shadow-[var(--shadow-sm)]`}
      data-testid="equipes-recruiter-card"
    >
      <RecruiterAvatar card={card} size={36} bg={avatarBg} borderColor={avatarBorder} />
      <div className="min-w-0 flex-1">
        <div className="break-words text-sm font-semibold leading-tight" title={card.name}>{card.name}</div>
        {periodMulti
          ? <PeriodStat card={card} />
          : <div className="mt-1"><PresenceBadge card={card} canEdit={canEdit} onToggle={onToggle} /></div>}
        {!periodMulti && (
          <SpotJourEditor personId={card.rd_id} personType="recruiter" value={card.spot_du_jour}
            source={card.spot_source} canEdit={canEdit} date={spotDate} onSaved={onSaveSpot} />
        )}
      </div>
      {canEdit && (
        <div className="flex shrink-0 items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="grid h-8 w-8 place-items-center rounded-lg text-[rgba(24,24,24,0.5)] hover:bg-[var(--surface-2)] hover:text-[var(--shariti-ink)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(238,114,46,0.22)]"
                aria-label={`Options ${card.name}`} data-testid="equipes-recruiter-menu">
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onPhoto(card)} data-testid="equipes-recruiter-photo-open"><ImagePlus className="mr-2 h-4 w-4" />Photo…</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onOffRoster(card)} className="text-[#B26A00]" data-testid="equipes-recruiter-offroster"><UserMinus className="mr-2 h-4 w-4" />Retirer des effectifs</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            {...listeners} {...attributes}
            className="grid h-9 w-9 cursor-grab touch-none place-items-center rounded-lg border border-[var(--border-soft)] bg-[var(--bg-subtle)] text-[rgba(24,24,24,0.5)] hover:text-[var(--shariti-ink)] active:cursor-grabbing focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(238,114,46,0.22)]"
            aria-label={`Déplacer ${card.name}`}
            data-testid="equipes-recruiter-drag-handle"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Column (droppable)                                                 */
/* ------------------------------------------------------------------ */
const Column = ({ droppableId, children, className = "", ...rest }) => {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });
  return (
    <div ref={setNodeRef} {...rest}
      className={`w-[300px] shrink-0 sm:w-[330px] ${className} ${isOver ? "ring-2 ring-[rgba(238,114,46,0.45)]" : ""} rounded-2xl transition-shadow`}>
      {children}
    </div>
  );
};

/* ================================================================== */
/* Page                                                               */
/* ================================================================== */
export default function Equipes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Édition autorisée pour les admins, ET pour un compte « lecture seule » à qui
  // l'on a explicitement accordé la permission « Gérer les équipes » (back-office).
  const canEdit = user?.role === "admin" || !!user?.permissions?.manage_teams;

  const [mode, setMode] = useState("jour");
  const [anchor, setAnchor] = useState(new Date());
  const [range, setRange] = useState(undefined);
  const [calOpen, setCalOpen] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCard, setActiveCard] = useState(null);
  // Ordre vivant des colonnes pour le glisser-déposer triable (multi-colonnes) :
  // { unassigned: [rd_id...], [chef_id]: [rd_id...] }. Dérivé de ``data`` puis muté
  // en optimiste pendant le drag.
  const [containers, setContainers] = useState({});
  const startContainerRef = useRef(null);

  // dialogs
  const [chefDlg, setChefDlg] = useState(null); // {id?, nom, color} or null
  const [recruiterDlg, setRecruiterDlg] = useState(null); // {chef_equipe_id|null} or null
  const [removeChef, setRemoveChef] = useState(null);
  const [photoTarget, setPhotoTarget] = useState(null);   // recruiter card
  const [offTarget, setOffTarget] = useState(null);       // recruiter card (confirm retrait)
  const [spotsOpen, setSpotsOpen] = useState(false);
  const [showOff, setShowOff] = useState(false);
  // Portée de la carte des équipes : "prive" (sites privés) ou "pap" (porte-à-porte).
  const [scope, setScope] = useState("prive");

  const { start, end } = periodRange(mode, anchor, range);
  const canQuery = mode !== "perso" || (range?.from && range?.to);
  const periodMulti = !!data?.period_multi;

  const load = async () => {
    if (!canQuery) { setLoading(false); return; }
    try {
      const res = await api.get("/teams/overview", { params: { start: isoDay(start), end: isoDay(end), scope } });
      setData(res.data);
    } catch (e) { toast.error("Impossible de charger les équipes"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); load(); }, [mode, anchor, range, scope]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const allCards = useMemo(() => {
    if (!data) return {};
    const m = {};
    (data.chefs || []).forEach((c) => c.recruiters.forEach((r) => { m[r.rd_id] = { ...r, chefColor: c.color }; }));
    (data.unassigned || []).forEach((r) => { m[r.rd_id] = { ...r, chefColor: null }; });
    return m;
  }, [data]);

  // (Re)dérive l'ordre vivant des colonnes à chaque rechargement des données.
  useEffect(() => {
    if (!data) { setContainers({}); return; }
    const c = { unassigned: (data.unassigned || []).map((r) => r.rd_id) };
    (data.chefs || []).forEach((ch) => { c[ch.id] = (ch.recruiters || []).map((r) => r.rd_id); });
    setContainers(c);
  }, [data]);

  // Retrouve la colonne contenant un id (ou l'id s'il s'agit lui-même d'une colonne).
  const findContainer = (id) => {
    if (id == null) return null;
    if (id in containers) return id;
    return Object.keys(containers).find((k) => (containers[k] || []).includes(id)) || null;
  };

  const onDragStart = (e) => {
    setActiveCard(allCards[e.active.id] || null);
    startContainerRef.current = findContainer(e.active.id);
  };

  // Pendant le survol : déplace la carte active vers la colonne survolée (reflow visuel).
  const onDragOver = (e) => {
    const { active, over } = e;
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;
    const from = findContainer(activeId);
    const to = findContainer(overId);
    if (!from || !to || from === to) return;
    setContainers((prev) => {
      const fromItems = prev[from] || [];
      const toItems = prev[to] || [];
      let newIndex;
      if (overId in prev) {
        newIndex = toItems.length; // survol du conteneur (zone vide) → fin
      } else {
        const overIndex = toItems.indexOf(overId);
        newIndex = overIndex >= 0 ? overIndex : toItems.length;
      }
      return {
        ...prev,
        [from]: fromItems.filter((i) => i !== activeId),
        [to]: [...toItems.slice(0, newIndex), activeId, ...toItems.slice(newIndex)],
      };
    });
  };

  // Persiste le changement de colonne et/ou le nouvel ordre.
  const persistMove = async (from, to, cont, movedId) => {
    try {
      if (from !== to) {
        const chefId = to === "unassigned" ? null : to;
        await api.patch(`/teams/recruiter/${movedId}/chef`, { chef_equipe_id: chefId, scope });
      }
      // Mémorise l'ordre UNIQUEMENT pour les colonnes d'équipe (le vivier « À affecter »
      // reste trié alphabétiquement côté serveur).
      if (to !== "unassigned") {
        await api.post("/teams/reorder", { scope, chef_equipe_id: to, ordered_rd_ids: cont[to] || [] });
      }
      toast.success(from !== to ? "Recruteur déplacé" : "Ordre mis à jour");
    } catch (err) {
      toast.error("Modification impossible");
      load();
    }
  };

  // Fin du glisser-déposer : on calcule l'état final de façon SYNCHRONE (sans effet de
  // bord dans le setState — StrictMode exécute les updaters deux fois en dev) puis on
  // persiste une seule fois.
  const onDragEnd = (e) => {
    const { active, over } = e;
    const activeId = active.id;
    setActiveCard(null);
    const from = startContainerRef.current;
    startContainerRef.current = null;
    if (!over) return;
    const to = findContainer(over.id);
    if (!to) return;
    const items = containers[to] || [];
    const oldIndex = items.indexOf(activeId);
    let newIndex;
    if (over.id in containers) newIndex = items.length - 1;
    else { const oi = items.indexOf(over.id); newIndex = oi >= 0 ? oi : items.length - 1; }
    const reordered = (oldIndex >= 0 && newIndex >= 0) ? arrayMove(items, oldIndex, newIndex) : items;
    const next = { ...containers, [to]: reordered };
    setContainers(next);
    // Rien n'a bougé (même colonne, même position) → pas d'appel réseau.
    if (from === to && oldIndex === newIndex) return;
    persistMove(from, to, next, activeId);
  };

  const togglePresence = async (card) => {
    const next = card.present ? "absent" : "present";
    try {
      const res = await api.post("/teams/presence", { rd_id: card.rd_id, status: next });
      setData((prev) => {
        if (!prev) return prev;
        const n = JSON.parse(JSON.stringify(prev));
        const apply = (r) => { if (r.rd_id === card.rd_id) { r.present = res.data.present; r.presence_status = res.data.presence_status; r.presence_source = res.data.presence_source; } };
        n.chefs.forEach((c) => c.recruiters.forEach(apply));
        n.unassigned.forEach(apply);
        const delta = res.data.present ? 1 : -1;
        n.kpis.presents = Math.max(0, n.kpis.presents + delta);
        n.kpis.absents = Math.max(0, n.kpis.absents - delta);
        return n;
      });
    } catch { toast.error("Bascule de présence impossible"); }
  };

  const shift = (dir) => {
    if (mode === "jour") setAnchor((a) => addDays(a, dir));
    else if (mode === "semaine") setAnchor((a) => addWeeks(a, dir));
    else if (mode === "mois") setAnchor((a) => addMonths(a, dir));
    else if (mode === "annee") setAnchor((a) => addYears(a, dir));
  };

  const doOffRoster = async (card, off) => {
    try {
      await api.post(`/teams/recruiter/${card.rd_id}/roster`, { off_roster: off });
      toast.success(off ? `${card.name} retiré des effectifs` : `${card.name} réintégré`);
      setOffTarget(null); load();
    } catch { toast.error("Erreur"); }
  };

  const k = data?.kpis || {};
  const offList = data?.off_roster || [];
  // Jour sélectionné pour le « spot du jour » (renseigné par le backend en vue jour).
  const spotDate = data?.start;
  // Applique en local la nouvelle valeur du spot du jour (recruteur OU chef).
  const applySpotJour = (personId, spot, source) => {
    setData((prev) => {
      if (!prev) return prev;
      const n = JSON.parse(JSON.stringify(prev));
      const applyR = (r) => { if (r.rd_id === personId) { r.spot_du_jour = spot; r.spot_source = source; } };
      n.chefs.forEach((c) => {
        if (c.id === personId) { c.spot_du_jour = spot; c.spot_source = source; }
        c.recruiters.forEach(applyR);
      });
      (n.unassigned || []).forEach(applyR);
      return n;
    });
  };

  return (
    <>
      <PageHeader
        title="Équipes"
        subtitle="Vos équipes terrain d'un coup d'œil : qui est dans quelle équipe, qui est présent, et les spots à couvrir."
        action={canEdit ? (
          <Button onClick={() => setChefDlg({ nom: "", color: CHEF_COLORS[0] })}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="equipes-add-lead-open">
            <Plus className="mr-1.5 h-4 w-4" />{scope === "pap" ? "Ajouter un responsable" : "Ajouter un chef d'équipe"}
          </Button>
        ) : null}
      >
        {/* Portée : sites privés vs porte-à-porte (équipes indépendantes) */}
        <div className="mb-3">
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList data-testid="equipes-scope-tabs">
              <TabsTrigger value="prive" data-testid="equipes-scope-prive">Sites privés</TabsTrigger>
              <TabsTrigger value="pap" data-testid="equipes-scope-pap">Porte-à-porte</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {/* Période */}
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList data-testid="equipes-period-tabs">
              {PERIOD_MODES.map((m) => <TabsTrigger key={m.v} value={m.v} data-testid={`equipes-period-${m.v}`}>{m.l}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          {mode !== "perso" ? (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8 border-[var(--border-soft)] bg-white/70" onClick={() => shift(-1)} data-testid="equipes-period-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="min-w-[10rem] text-center text-sm font-bold capitalize" data-testid="equipes-period-label">{periodLabel(mode, anchor, range)}</span>
              <Button variant="outline" size="icon" className="h-8 w-8 border-[var(--border-soft)] bg-white/70" onClick={() => shift(1)} data-testid="equipes-period-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          ) : (
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="border-[var(--border-soft)] bg-white/70" data-testid="equipes-period-custom-button">
                  <CalendarDays className="mr-1.5 h-3.5 w-3.5" />{periodLabel("perso", anchor, range)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={2} weekStartsOn={1} locale={frLocale} defaultMonth={range?.from || anchor} />
              </PopoverContent>
            </Popover>
          )}
          <Button variant="ghost" size="sm" className="ml-auto text-muted-ink" onClick={() => { setLoading(true); load(); }} data-testid="equipes-refresh">
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />Actualiser
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <KpiCard label="Collaborateurs" value={loading ? "—" : k.total_collaborateurs ?? 0} icon={Users} accent="border-l-[var(--shariti-orange)]" testid="equipes-kpi-total" />
          <KpiCard label="Présents (aujourd'hui)" value={loading ? "—" : k.presents ?? 0} icon={CheckCircle2} accent="border-l-[#1F9D55]" testid="equipes-kpi-present" />
          <KpiCard label="Absents (aujourd'hui)" value={loading ? "—" : k.absents ?? 0} icon={MinusCircle} accent="border-l-[rgba(24,24,24,0.2)]" testid="equipes-kpi-absent" />
          <KpiCard label="Équipes" value={loading ? "—" : k.equipes ?? 0} icon={Network} accent="border-l-[var(--shariti-yellow)]" testid="equipes-kpi-teams" />
          <KpiCard label="Spots non couverts" value={loading ? "—" : k.spots_non_couverts ?? 0} hint={loading ? "" : `sur ${k.spots_total ?? 0} journée(s)-spot · voir la liste`} icon={AlertTriangle} accent="border-l-[#D64545]" testid="equipes-kpi-uncovered" onClick={() => setSpotsOpen(true)} />
        </div>
      </PageHeader>

      <PageBody>
        {!canEdit && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-[rgba(47,111,237,0.18)] bg-[rgba(47,111,237,0.08)] px-3 py-2 text-sm text-[#1F3D8A]" data-testid="equipes-readonly-banner">
            <Info className="h-4 w-4 shrink-0" />Mode consultation — les modifications sont désactivées.
          </div>
        )}

        {loading ? (
          <div className="flex gap-4 overflow-hidden" data-testid="equipes-loading">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="w-[300px] shrink-0 space-y-3 rounded-2xl border border-[var(--border-soft)] bg-white p-3 sm:w-[330px]">
                <Skeleton className="h-6 w-32" />
                {[0, 1, 2].map((j) => <Skeleton key={j} className="h-16 w-full rounded-xl" />)}
              </div>
            ))}
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd}>
            <div className="flex gap-4 overflow-x-auto pb-3" data-testid="equipes-board">
              {/* Colonne À affecter */}
              <Column droppableId="unassigned" data-testid="equipes-unassigned-column">
                <UnassignedColumn ids={containers.unassigned || []} allCards={allCards} canEdit={canEdit}
                  periodMulti={periodMulti} spotDate={spotDate} scope={scope} onToggle={togglePresence}
                  onPhoto={setPhotoTarget} onOffRoster={setOffTarget} onSaveSpot={applySpotJour}
                  onAdd={() => setRecruiterDlg({ chef_equipe_id: null })} onReactivated={() => { setLoading(true); load(); }} />
              </Column>

              {/* Colonnes chefs */}
              {(data?.chefs || []).map((chef) => {
                const ids = containers[chef.id] || [];
                return (
                <Column key={chef.id} droppableId={chef.id}>
                  <div className="flex h-full flex-col rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]" data-testid="equipes-team-column">
                    <div className="flex items-center justify-between gap-2 px-3 pb-2 pt-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: chef.color }} />
                        <span className="break-words text-sm font-bold" title={chef.nom}>{chef.nom}</span>
                        <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-bold text-muted-ink">{ids.length}</span>
                      </div>
                      {canEdit && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Actions équipe" data-testid={`equipes-team-menu-${chef.id}`}><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setRecruiterDlg({ chef_equipe_id: chef.id })} data-testid="equipes-team-add-recruiter"><UserPlus className="mr-2 h-4 w-4" />Ajouter un recruteur</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setChefDlg({ id: chef.id, nom: chef.nom, color: chef.color })} data-testid="equipes-rename-open"><Pencil className="mr-2 h-4 w-4" />Renommer</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setRemoveChef(chef)} className="text-[#D64545]"><Trash2 className="mr-2 h-4 w-4" />Retirer</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                    {!periodMulti && (
                      <div className="px-3 pb-2" data-testid={`equipes-chef-spot-${chef.id}`}>
                        <SpotJourEditor personId={chef.id} personType="chef" value={chef.spot_du_jour}
                          source={chef.spot_source} canEdit={canEdit} date={spotDate} onSaved={applySpotJour} />
                      </div>
                    )}
                    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                      <div className="flex flex-1 flex-col gap-2 px-3 pb-3">
                        {ids.length === 0 && (
                          <div className="rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--bg-subtle)] p-3 text-center text-xs text-muted-ink">
                            {canEdit ? "Glissez un recruteur ici" : "Aucun recruteur"}
                          </div>
                        )}
                        {ids.map((rid) => {
                          const r = allCards[rid];
                          if (!r) return null;
                          return (
                            <RecruiterCard key={rid} card={r} chefColor={chef.color} canEdit={canEdit}
                              periodMulti={periodMulti} spotDate={spotDate} onToggle={togglePresence}
                              onPhoto={setPhotoTarget} onOffRoster={setOffTarget} onSaveSpot={applySpotJour} />
                          );
                        })}
                      </div>
                    </SortableContext>
                  </div>
                </Column>
                );
              })}

              {(data?.chefs || []).length === 0 && canEdit && (
                <div className="grid w-[300px] shrink-0 place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white p-6 text-center sm:w-[330px]">
                  <Network className="mb-2 h-8 w-8 text-[var(--shariti-orange)]" />
                  <p className="mb-3 text-sm text-muted-ink">Aucune équipe pour l'instant.</p>
                  <Button size="sm" onClick={() => setChefDlg({ nom: "", color: CHEF_COLORS[0] })} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]"><Plus className="mr-1.5 h-4 w-4" />Ajouter un chef</Button>
                </div>
              )}
            </div>

            <DragOverlay>
              {activeCard ? (
                <div className="flex items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-white p-2.5 shadow-[var(--shadow-lg)]">
                  <RecruiterAvatar card={activeCard} size={36} />
                  <div className="text-sm font-semibold">{activeCard.name}</div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        {/* Sortis des effectifs (repliable) */}
        {!loading && offList.length > 0 && (
          <div className="mt-5" data-testid="equipes-offroster-section">
            <Collapsible open={showOff} onOpenChange={setShowOff}>
              <CollapsibleTrigger asChild>
                <button className="flex w-full items-center justify-between rounded-xl border border-[var(--border-soft)] bg-white px-4 py-3 text-left transition-colors hover:bg-[var(--surface-2)]" data-testid="equipes-offroster-toggle">
                  <span className="flex items-center gap-2 text-sm font-bold">
                    <UserMinus className="h-4 w-4 text-muted-ink" />Sortis des effectifs
                    <span className="rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-bold text-muted-ink">{offList.length}</span>
                  </span>
                  <ChevronDown className={`h-4 w-4 text-muted-ink transition-transform duration-200 ${showOff ? "rotate-180" : ""}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {offList.map((o) => (
                    <div key={o.rd_id} className="flex items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-white p-2.5" data-testid="equipes-offroster-card">
                      <RecruiterAvatar card={o} size={34} />
                      <div className="min-w-0 flex-1">
                        <div className="break-words text-sm font-semibold">{o.name}</div>
                        <Badge variant="outline" className="mt-0.5 border-[rgba(24,24,24,0.12)] bg-[rgba(24,24,24,0.05)] text-[10px] text-muted-ink">Sorti des effectifs</Badge>
                      </div>
                      {canEdit && (
                        <Button variant="outline" size="sm" className="shrink-0 border-[var(--border-soft)]" onClick={() => doOffRoster(o, false)} data-testid="equipes-reintegrate">
                          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Réintégrer
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </PageBody>

      {/* Dialog : ajouter / renommer un chef */}
      <ChefDialog value={chefDlg} scope={scope} onClose={() => setChefDlg(null)} onSaved={() => { setChefDlg(null); load(); }} />
      {/* Dialog : ajouter un recruteur */}
      <RecruiterDialog value={recruiterDlg} chefs={data?.chefs || []} scope={scope} onClose={() => setRecruiterDlg(null)} onSaved={() => { setRecruiterDlg(null); load(); }} />
      {/* Retirer un chef */}
      <AlertDialog open={!!removeChef} onOpenChange={(o) => !o && setRemoveChef(null)}>
        <AlertDialogContent data-testid="equipes-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer l'équipe « {removeChef?.nom} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le chef d'équipe sera retiré et ses {removeChef?.count || 0} recruteur(s) repasseront dans « À affecter ». Cette action est réversible en le recréant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-[#D64545] text-white hover:bg-[#B93A3A]" data-testid="equipes-remove-confirm"
              onClick={async () => { try { await api.delete(`/teams/chef/${removeChef.id}`); toast.success("Équipe retirée"); setRemoveChef(null); load(); } catch { toast.error("Erreur"); } }}>
              Retirer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Photo d'un recruteur */}
      <PhotoDialog value={photoTarget} onClose={() => setPhotoTarget(null)} onSaved={() => { setPhotoTarget(null); load(); }} />

      {/* Retirer des effectifs (confirmation) */}
      <AlertDialog open={!!offTarget} onOpenChange={(o) => !o && setOffTarget(null)}>
        <AlertDialogContent data-testid="equipes-offroster-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer « {offTarget?.name} » des effectifs ?</AlertDialogTitle>
            <AlertDialogDescription>
              Ce collaborateur ne fera plus partie des équipes : il disparaîtra du board et sera détaché de son équipe. Vous pourrez le <strong>réintégrer</strong> à tout moment depuis « Sortis des effectifs ».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-[#B26A00] text-white hover:bg-[#96590A]" data-testid="equipes-offroster-confirm"
              onClick={() => doOffRoster(offTarget, true)}>
              Retirer des effectifs
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Spots non couverts */}
      <SpotsDialog open={spotsOpen} onClose={() => setSpotsOpen(false)} start={isoDay(start)} end={isoDay(end)}
        periodLabel={periodLabel(mode, anchor, range)} canEdit={canEdit} navigate={navigate} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* À affecter (vivier + recherche + réactivation des désactivés)       */
/* ------------------------------------------------------------------ */
const UnassignedColumn = ({ ids, allCards, canEdit, periodMulti, spotDate, scope, onToggle, onPhoto, onOffRoster, onAdd, onSaveSpot, onReactivated }) => {
  const [q, setQ] = useState("");
  // Recherche serveur des collaborateurs INVISIBLES (désactivés / sortis / archivés)
  // pour pouvoir les réactiver directement — déclenchée seulement quand on tape.
  const [inactive, setInactive] = useState([]);
  const [searchingInactive, setSearchingInactive] = useState(false);
  const [reactivating, setReactivating] = useState(null);
  const debounceRef = useRef(null);

  const query = q.trim().toLowerCase();
  const visibleIds = ids.filter((rid) => !query || (allCards[rid]?.name || "").toLowerCase().includes(query));

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!canEdit || query.length < 2) { setInactive([]); setSearchingInactive(false); return; }
    setSearchingInactive(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get("/teams/recruiters/search-inactive", { params: { q: query } });
        setInactive(res.data || []);
      } catch { setInactive([]); }
      finally { setSearchingInactive(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, canEdit]);

  const REASON_LABEL = { desactive: "Désactivé", off_roster: "Sorti des effectifs", archive: "Archivé" };
  const reactivate = async (r) => {
    setReactivating(r.rd_id);
    try {
      await api.post(`/teams/recruiter/${r.rd_id}/reactivate`, { scope });
      toast.success(`${r.name} réactivé(e) — ajouté(e) à « À affecter »`);
      setQ("");
      onReactivated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Réactivation impossible"); }
    finally { setReactivating(null); }
  };

  return (
    <div className="flex h-full flex-col rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--bg-subtle)] shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between gap-2 px-3 pb-2 pt-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold">À affecter</span>
          <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold text-muted-ink">{ids.length}</span>
        </div>
        {canEdit && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onAdd} aria-label="Ajouter un recruteur" data-testid="equipes-add-recruiter-open"><UserPlus className="h-4 w-4" /></Button>
        )}
      </div>
      <div className="px-3 pb-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (même désactivé)…" className="h-8 bg-white pl-8 text-sm" data-testid="equipes-unassigned-search-input" />
        </div>
      </div>
      <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
        <div className="flex flex-1 flex-col gap-2 px-3 pb-3">
          {visibleIds.length === 0 && inactive.length === 0 && !searchingInactive && (
            <div className="rounded-xl border border-dashed border-[var(--border-soft)] bg-white p-3 text-center text-xs text-muted-ink">
              {query ? "Aucun collaborateur actif à ce nom." : "Personne à affecter."}
            </div>
          )}
          {visibleIds.map((rid) => {
            const r = allCards[rid];
            if (!r) return null;
            return (
              <RecruiterCard key={rid} card={r} chefColor={null} canEdit={canEdit} periodMulti={periodMulti}
                spotDate={spotDate} onToggle={onToggle} onPhoto={onPhoto} onOffRoster={onOffRoster} onSaveSpot={onSaveSpot} />
            );
          })}

          {/* Désactivés / sortis / archivés correspondant à la recherche → réactivables */}
          {canEdit && query.length >= 2 && (searchingInactive || inactive.length > 0) && (
            <div className="mt-1 rounded-xl border border-[rgba(178,106,0,0.25)] bg-[rgba(178,106,0,0.06)] p-2" data-testid="equipes-inactive-section">
              <div className="mb-1.5 flex items-center gap-1.5 px-0.5 text-[10px] font-bold uppercase tracking-wide text-[#8a4b1f]">
                <UserCheck className="h-3.5 w-3.5" />Désactivés — à réactiver
              </div>
              {searchingInactive && inactive.length === 0 ? (
                <div className="py-2 text-center text-[11px] text-muted-ink"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>
              ) : (
                <div className="space-y-1.5">
                  {inactive.map((r) => (
                    <div key={r.rd_id} className="flex items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-white p-2" data-testid="equipes-inactive-card">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[var(--border-soft)] bg-[var(--surface-2)] text-[10px] font-bold">{initials(r.name)}</span>
                      <div className="min-w-0 flex-1">
                        <div className="break-words text-[13px] font-semibold leading-tight">{r.name}</div>
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {(r.reasons || []).map((rs) => (
                            <span key={rs} className="rounded bg-[var(--surface-2)] px-1 py-0.5 text-[9px] font-bold text-muted-ink">{REASON_LABEL[rs] || rs}</span>
                          ))}
                        </div>
                      </div>
                      <Button size="sm" className="h-7 shrink-0 bg-[#B26A00] px-2 text-[11px] text-white hover:bg-[#96590A]"
                        disabled={reactivating === r.rd_id} onClick={() => reactivate(r)} data-testid={`equipes-reactivate-${r.rd_id}`}>
                        {reactivating === r.rd_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="mr-1 h-3.5 w-3.5" />Réactiver</>}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Dialog chef                                                        */
/* ------------------------------------------------------------------ */
const ChefDialog = ({ value, onClose, onSaved, scope = "prive" }) => {
  const [nom, setNom] = useState("");
  const [color, setColor] = useState(CHEF_COLORS[0]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (value) { setNom(value.nom || ""); setColor(value.color || CHEF_COLORS[0]); } }, [value]);
  const isEdit = !!value?.id;
  const isPap = scope === "pap";
  const submit = async () => {
    if (!nom.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      await api.post("/teams/chef", { id: value?.id, nom: nom.trim(), color, scope });
      toast.success(isEdit ? "Renommé" : "Ajouté");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const label = isPap ? "responsable" : "chef d'équipe";
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="equipes-chef-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Renommer le ${label}` : `Nouveau ${label}`}</DialogTitle>
          <DialogDescription>Nom affiché en tête de colonne et couleur de la pastille.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[11px]">Nom du {label} *</Label>
            <Input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Ex : Timothée" className="mt-1 h-9" data-testid="equipes-chef-name" autoFocus />
          </div>
          <div>
            <Label className="text-[11px]">Couleur</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {CHEF_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)} aria-label={`Couleur ${c}`}
                  className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${color === c ? "border-[var(--shariti-ink)]" : "border-transparent"}`}
                  style={{ background: c }} data-testid={`equipes-chef-color-${c}`} />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="equipes-add-lead-submit">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}{isEdit ? "Enregistrer" : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* Dialog recruteur (créer / rechercher)                              */
/* ------------------------------------------------------------------ */
const RecruiterDialog = ({ value, chefs, onClose, onSaved, scope = "prive" }) => {
  const [tab, setTab] = useState("create");
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const chefId = value?.chef_equipe_id ?? null;
  const chefName = chefs.find((c) => c.id === chefId)?.nom;

  useEffect(() => { if (value) { setTab("create"); setName(""); setQ(""); setResults([]); } }, [value]);
  useEffect(() => {
    if (tab !== "search") return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try { const res = await api.get("/teams/recruiters/search", { params: { q } }); setResults(res.data); }
      catch { setResults([]); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q, tab]);

  const createNew = async () => {
    if (!name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try { await api.post("/teams/recruiter", { name: name.trim(), chef_equipe_id: chefId, scope }); toast.success("Recruteur ajouté"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const attachExisting = async (rd) => {
    setSaving(true);
    try { await api.post("/teams/recruiter", { rd_id: rd.rd_id, chef_equipe_id: chefId, scope }); toast.success(`${rd.name} rattaché`); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="equipes-recruiter-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter un recruteur</DialogTitle>
          <DialogDescription>{chefName ? <>Équipe : <strong>{chefName}</strong></> : "Sera placé dans « À affecter »."}</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="create" className="flex-1" data-testid="equipes-recruiter-tab-create">Nouveau</TabsTrigger>
            <TabsTrigger value="search" className="flex-1" data-testid="equipes-recruiter-tab-search">Existant</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "create" ? (
          <div className="space-y-2 pt-1">
            <Label className="text-[11px]">Nom du recruteur *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Mickaël" className="h-9" data-testid="equipes-add-recruiter-create" autoFocus />
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={createNew} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="equipes-add-recruiter-submit">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Ajouter
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-2 pt-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un recruteur existant…" className="h-9 pl-8" data-testid="equipes-add-recruiter-search" autoFocus />
            </div>
            <div className="max-h-64 space-y-1 overflow-y-auto">
              {results.length === 0 && <div className="py-6 text-center text-xs text-muted-ink">Aucun résultat.</div>}
              {results.map((r) => (
                <button key={r.rd_id} onClick={() => attachExisting(r)} disabled={saving}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--border-soft)] bg-white p-2 text-left hover:bg-[var(--surface-2)] disabled:opacity-50"
                  data-testid="equipes-add-recruiter-result">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[var(--border-soft)] bg-[var(--surface-2)] text-[10px] font-bold">{initials(r.name)}</span>
                    <span className="break-words text-sm font-medium">{r.name}</span>
                  </span>
                  {r.actif_terrain ? <Badge variant="outline" className="shrink-0 text-[10px]">Terrain</Badge> : null}
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* Dialog photo recruteur (upload + lien)                             */
/* ------------------------------------------------------------------ */
const PhotoDialog = ({ value, onClose, onSaved }) => {
  const [tab, setTab] = useState("upload");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { if (value) { setTab("upload"); setUrl(value.photo_url || ""); } }, [value]);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choisissez une image"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/teams/recruiter/${value.rd_id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo mise à jour");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'upload"); }
    finally { setBusy(false); }
  };
  const doLink = async () => {
    if (!url.trim().startsWith("http")) { toast.error("Lien d'image invalide"); return; }
    setBusy(true);
    try { await api.post(`/teams/recruiter/${value.rd_id}/photo-link`, { photo_url: url.trim() }); toast.success("Photo mise à jour"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const doRemove = async () => {
    setBusy(true);
    try { await api.delete(`/teams/recruiter/${value.rd_id}/photo`); toast.success("Photo retirée"); onSaved(); }
    catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };
  const hasPhoto = !!(value?.photo_url || value?.photo_path);

  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="equipes-photo-dialog">
        <DialogHeader>
          <DialogTitle>Photo — {value?.name}</DialogTitle>
          <DialogDescription>Ajoutez une photo pour une carte plus vivante et reconnaissable.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center pb-1">
          {value ? <RecruiterAvatar card={value} size={72} /> : null}
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="upload" className="flex-1" data-testid="equipes-photo-tab-upload"><Upload className="mr-1.5 h-3.5 w-3.5" />Upload</TabsTrigger>
            <TabsTrigger value="link" className="flex-1" data-testid="equipes-photo-tab-link"><Link2 className="mr-1.5 h-3.5 w-3.5" />Lien</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "upload" ? (
          <div className="space-y-2 pt-1">
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => doUpload(e.target.files?.[0])} data-testid="equipes-photo-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={busy} className="w-full bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="equipes-photo-upload-btn">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-1.5 h-4 w-4" />}Choisir une image…
            </Button>
            <p className="text-center text-[10px] text-muted-ink">JPEG, PNG, WEBP — max 5 Mo</p>
          </div>
        ) : (
          <div className="space-y-2 pt-1">
            <Label className="text-[11px]">Lien de l'image</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="h-9" data-testid="equipes-photo-link-input" />
            <div className="flex justify-end">
              <Button onClick={doLink} disabled={busy} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="equipes-photo-link-btn">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Enregistrer le lien
              </Button>
            </div>
          </div>
        )}
        <DialogFooter className="sm:justify-between">
          {hasPhoto ? (
            <Button variant="ghost" onClick={doRemove} disabled={busy} className="text-[#D64545] hover:bg-[rgba(214,69,69,0.08)]" data-testid="equipes-photo-remove-btn">
              <Trash2 className="mr-1.5 h-4 w-4" />Retirer la photo
            </Button>
          ) : <span />}
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* Dialog spots non couverts (liste cliquable)                        */
/* ------------------------------------------------------------------ */
const SpotsDialog = ({ open, onClose, start, end, periodLabel, canEdit, navigate }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!open) return;
    setRows(null);
    api.get("/teams/spots-non-couverts", { params: { start, end } })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]));
  }, [open, start, end]);

  const go = (path) => { onClose(); navigate(path); };
  const STATUS = { ferme: "Ferme", au_crayon: "Au crayon", proposition_envoyee: "Proposition", a_confirmer: "À confirmer" };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="equipes-spots-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[#D64545]" />Spots non couverts</DialogTitle>
          <DialogDescription>Journées-spots sans recruteur affecté — <span className="capitalize">{periodLabel}</span>. Cliquez pour agir.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {rows === null && [0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
          {rows && rows.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-ink" data-testid="equipes-spots-empty">
              <CheckCircle2 className="h-8 w-8 text-[#1F9D55]" />Tous les spots de la période sont couverts. 🎉
            </div>
          )}
          {rows && rows.map((s) => (
            <div key={s.booking_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid="equipes-spot-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="break-words text-sm font-bold">{s.spot_nom}</span>
                  {s.status ? <Badge variant="outline" className="shrink-0 text-[10px]">{STATUS[s.status] || s.status}</Badge> : null}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-ink">
                  <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{s.date ? fmtDate(new Date(s.date), "EEE d MMM", { locale: frLocale }) : "—"}</span>
                  {s.ville ? <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{s.ville}</span> : null}
                  {s.cdp_nom ? <span>CDP&nbsp;: {s.cdp_nom}</span> : null}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {canEdit && s.spot_id && (
                  <Button variant="outline" size="sm" className="border-[var(--border-soft)]" onClick={() => go(`/spots/${s.spot_id}`)} data-testid="equipes-spot-fiche">
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />Fiche
                  </Button>
                )}
                <Button variant="outline" size="sm" className="border-[var(--border-soft)]" onClick={() => go(`/planning?date=${s.date}`)} data-testid="equipes-spot-planning">
                  <CalendarDays className="mr-1.5 h-3.5 w-3.5" />Planning
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
